/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { useEffect } from "react";
import type { RefObject } from "react";

/**
 * Keeps a text input's real DOM focus in step with its D-pad focus state, so
 * the platform keyboard attaches and VoiceView reads the field.
 *
 * Losing focus is deferred until this commit's effects have run, and the
 * input is blurred only if nothing else took focus. Blurring straight away
 * parks focus on <body> before the next control is focused, and VoiceView can
 * drop the second focus event (see useAccessibilityFocusSync). Promise rather
 * than queueMicrotask for older Tizen/webOS Chromium.
 *
 * @param inputRef - Ref to the input element.
 * @param isFocused - True when the input should hold real DOM focus.
 */
export function useInputFocusSync(
  inputRef: RefObject<HTMLInputElement | null>,
  isFocused: boolean
): void {
  useEffect((): void => {
    const input: HTMLInputElement | null = inputRef.current;
    if (input === null) {
      return;
    }
    if (isFocused) {
      input.focus();
      return;
    }
    Promise.resolve().then((): void => {
      if (document.activeElement === input) {
        input.blur();
      }
    });
  }, [inputRef, isFocused]);
}
